import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import i18n from 'i18n/i18n';
import qs from 'qs';
import { toast } from 'shared/components/ui/use-toast';
import { auth, environment, Environment, toApiHost } from 'shared/config/firebase';
import JwtAuthService from './JwtAuthService';
import FirebaseTokenService from './FirebaseTokenService';
import { createLogger } from 'shared/logger';

const log = createLogger('RestService');

interface RestResponse<T> {
  resource?: T;
  error?: any;
}

const BACKEND_API_BASE_URL = window.__APP_CONFIG__?.BACKEND_API_BASE_URL;

// Best-effort correlation id from the backend's response headers so a failed
// call in the browser console can be joined to the server-side request log.
// The FastAPI backend uses starlette-context (X-Request-ID / X-Correlation-ID);
// if none is echoed back this is simply undefined and omitted from the log.
function extractRequestId(headers: any): string | undefined {
  if (!headers) return undefined;
  const get = (k: string): string | undefined =>
    typeof headers.get === 'function' ? headers.get(k) : headers[k];
  return (
    get('x-request-id') ||
    get('x-correlation-id') ||
    get('x-cloud-trace-context') ||
    undefined
  );
}

function getHttpErrorMessage(status: number, fallback?: string): string {
  const translationKey = `global.httpErrors.${status}`;
  const translated = i18n.t(translationKey);
  // If translation exists (not returning the key itself), use it
  if (translated !== translationKey) {
    return `${status} - ${translated}`;
  }
  // Fallback to provided text or generic error
  return fallback ? `${status} - ${fallback}` : `${status}`;
}

export class ApiResponseError extends Error {
  readonly data?: any;
  readonly status: number;
  readonly cause: Error;
  readonly request: any;

  constructor(error: AxiosError, status: number, data?: any) {
    let msg: string;

    // Try to extract error message from response data
    if (typeof data === 'string' && data) {
      // Backends using PlainTextResponse / abort_plain (FastAPI) return the
      // error message as the raw body with Content-Type: text/plain.
      msg = data;
    } else if (data?.error?.error_message) {
      msg = data.error.error_message;
    } else if (data?.error?.message) {
      msg = data.error.message;
    } else if (data?.message) {
      msg = data.message;
    } else if (data?.detail) {
      msg = data.detail;
    } else if (status > 0) {
      // Use translated HTTP status message
      msg = getHttpErrorMessage(status, error.response?.statusText);
    } else {
      msg = error.message;
    }

    super(msg);
    this.name = 'ApiResponseError';
    this.cause = error;
    this.status = status;
    this.data = data;
    this.request = error?.request;
  }
}

class RestService {
  private ENVIRONMENT_APIS: { [key in Environment]: AxiosInstance } = {
    'streetbeat-production': this.createAPIInstance('streetbeat-production'),
    'streetbeat-staging': this.createAPIInstance('streetbeat-staging'),
    'from-host': this.createAPIInstance('from-host'),
  };

  private ENVIRONMENT_APIS_FORM_DATA: { [key in Environment]: AxiosInstance } = {
    'streetbeat-production': this.createAPIInstance('streetbeat-production', 'multipart/form-data'),
    'streetbeat-staging': this.createAPIInstance('streetbeat-staging', 'multipart/form-data'),
    'from-host': this.createAPIInstance('from-host', 'multipart/form-data'),
  };

  private createAPIInstance(environment: Environment, contentType: string = 'application/json') {
    let baseUrl = BACKEND_API_BASE_URL || `https://${toApiHost()}`;

    const instance = axios.create({
      baseURL: baseUrl,
    });
    this.addRequestInterceptor(instance, contentType);
    this.addResponseInterceptor(instance);
    return instance;
  }

  private async addRequestInterceptor(instance: AxiosInstance, contentType: string | null = 'application/json') {
    instance.interceptors.request.use(
      async (config) => {
        // Inject standard headers
        config.headers.Accept = 'application/json';
        config.headers['Content-Type'] = contentType || 'application/json';
        config.headers['AppVersionEpoch'] = '99999999999';
        config.headers['Domain'] = window.location.host;

        // Check if JWT authentication should be used
        if (JwtAuthService.shouldUseJwtAuth()) {
          const jwtToken = JwtAuthService.getToken();
          if (jwtToken) {
            config.headers['Jwt-Jwks'] = jwtToken;
            // Return early to avoid adding Firebase token
            return config;
          }
        }

        // Inject firebase token (only if JWT auth is not used)
        try {
          await auth.authStateReady();
          const token = await FirebaseTokenService.getCurrentToken();
          if (token) {
            config.headers.Firebase = token;
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          // Firebase token attach failed — the request still goes out, so this
          // is a warning, not a hard error.
          log.warn('failed to attach auth token to request', {
            url: config.url,
            error,
          });
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  private addResponseInterceptor(instance: AxiosInstance) {
    instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response && error.response.status === 401) {
          if (JwtAuthService.shouldUseJwtAuth()) {
            JwtAuthService.clearAuth();
            window.location.href = '/jwt-auth-required';
            return Promise.reject(new Error('Authentication required'));
          }
        }

        const suppressGlobalToast = Boolean(
          (error.config as any)?.suppressGlobalErrorToast,
        );

        const method = (error.config?.method || 'get').toUpperCase();
        const url = error.config?.url;

        if (!error.response) {
          const status = error.request?.status || 0;
          // RESULT of an API call: no response reached the client (network /
          // CORS / abort). Log method + url + status so the failure is
          // attributable without dumping the raw error.
          log.warn('API request failed (no response)', {
            method,
            url,
            status,
            message: error.message,
          });

          const isLikelyCorsBlockedError = status === 0 && error.message === 'Network Error';

          const apiError = new ApiResponseError(error, status, null);
          if (!suppressGlobalToast && ![401, 404].includes(status)) {
            toast({
              description: isLikelyCorsBlockedError
                ? i18n.t('global.serverError')
                : status > 0
                  ? getHttpErrorMessage(status)
                  : apiError.message,
              title: i18n.t('global.error'),
              variant: 'destructive',
            });
          }
          return Promise.reject(apiError);
        }

        const { status } = error.response;
        let { data } = error.response;

        if (data instanceof Blob && data.type === 'application/json') {
          try {
            const text = await data.text();
            data = JSON.parse(text);
          } catch {
            // Keep the original Blob data if parsing fails.
          }
        }

        const apiError = new ApiResponseError(error, status, data);
        // RESULT of an API call: the server rejected it. Log method + url +
        // status + backend correlation id (if any). 401/404 are routine (auth
        // redirect / optional resource) so they stay at warn; 5xx at error.
        const requestId = extractRequestId(error.response.headers);
        const failureContext = { method, url, status, requestId };
        if (status >= 500) {
          log.error('API request failed', failureContext);
        } else {
          log.warn('API request rejected', failureContext);
        }
        if (!suppressGlobalToast && ![401, 404].includes(error.request.status)) {
          toast({
            description: apiError.message,
            title: i18n.t('global.error'),
            variant: 'destructive',
          });
        }
        return Promise.reject(apiError);
      },
    );
  }

  async api(): Promise<AxiosInstance> {
    const env = await environment();
    if (env === 'from-host') {
      return this.ENVIRONMENT_APIS['from-host'];
    }
    if (!auth.currentUser) return this.ENVIRONMENT_APIS['production'];
    if (!auth.currentUser.email) return this.ENVIRONMENT_APIS['production'];

    return this.ENVIRONMENT_APIS[env];
  }

  async apiFormData(): Promise<AxiosInstance> {
    const env = await environment();
    if (env === 'from-host') {
      return this.ENVIRONMENT_APIS_FORM_DATA['from-host'];
    }
    if (!auth.currentUser) return this.ENVIRONMENT_APIS_FORM_DATA['production'];
    if (!auth.currentUser.email) return this.ENVIRONMENT_APIS_FORM_DATA['production'];

    return this.ENVIRONMENT_APIS_FORM_DATA[env];
  }

  async post<T>(url: string, payload?: any, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.post<RestResponse<T>>(url, payload, params);
    return data?.resource || (data as T);
  }

  async postFormData<T>(url: string, payload: FormData, params?: any): Promise<T> {
    const awaitedApi = await this.apiFormData();
    const { data } = await awaitedApi.post<RestResponse<T>>(url, payload, params);
    return data?.resource || (data as T);
  }

  async get<T>(url: string, params?: {}, options?: { suppressGlobalErrorToast?: boolean }): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.get<RestResponse<T>>(url, {
      ...options,
      params,
      paramsSerializer: (serializedParams) => qs.stringify(serializedParams, { arrayFormat: 'repeat' }),
    });
    return data?.resource || (data as T);
  }

  async put<T>(url: string, payload?: any, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.put<RestResponse<T>>(url, payload, {
      params,
    });
    return data?.resource || (data as T);
  }

  async patch<T>(url: string, payload?: any, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.patch<RestResponse<T>>(url, payload, params);
    return data?.resource || (data as T);
  }

  async delete<T>(url: string, params?: any): Promise<T> {
    const awaitedApi = await this.api();
    const { data } = await awaitedApi.delete<RestResponse<T>>(url, { params });
    return data?.resource || (data as T);
  }

  async download(url: string, params?: any): Promise<AxiosResponse<any, any>> {
    const awaitedApi = await this.api();
    const response = await awaitedApi.get<any>(url, { ...params, responseType: 'blob' });
    return response;
  }

  private createBaseAPIInstance(environment: Environment) {
    let baseUrl: string;
    if (environment === 'from-host') {
      baseUrl = `https://${toApiHost()}`;
    } else {
      baseUrl = BACKEND_API_BASE_URL || `https://${toApiHost()}`;
    }

    const instance = axios.create({
      baseURL: baseUrl,
    });
    this.addRequestInterceptor(instance);
    this.addResponseInterceptor(instance);
    return instance;
  }
  private ENVIRONMENT_BASE_APIS: { [key in Environment]: AxiosInstance } = {
    'streetbeat-production': this.createBaseAPIInstance('streetbeat-production'),
    'streetbeat-staging': this.createBaseAPIInstance('streetbeat-staging'),
    'from-host': this.createBaseAPIInstance('from-host'),
  };
  private async detectEnvironmentFromCurrentUser(): Promise<Environment> {
    return await environment();
  }
  async baseApi(): Promise<AxiosInstance> {
    const env = await this.detectEnvironmentFromCurrentUser();
    return this.ENVIRONMENT_BASE_APIS[env];
  }

  async postBaseDownload(url: string, payload?: any): Promise<AxiosResponse<any, any>> {
    const awaitedApi = await this.baseApi();
    const response = await awaitedApi.post<any>(url, payload, {
      responseType: 'blob',
    });
    return response;
  }
}

const restService = new RestService();
export default restService;
